import React, { useEffect, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";

// --- IMPORTS DES COMPOSANTS ---
import DarkVeil from "./components/Backgrounds/DarkVeil/DarkVeil";
import Header from "./components/Header";
import Footer from "./components/Footer";
import GradualBlur from "./components/Animations/GradualBlur/GradualBlur";
import MobileNavBar from "./components/MobileNavBar";
import MobileHeader from "./components/MobileHeader";
import { CarouselPartenaires } from "./components/CarouselPartenaires";
import ErrorBoundary from "./components/ErrorBoundary";
import useIsMobile from "./hooks/useIsMobile";

// --- LAZY LOADING DES PAGES ---
const Home = lazy(() => import("./Pages/Home"));
const Contact = lazy(() => import("./Pages/Contact"));
const WorkPage = lazy(() => import("./Pages/Work"));
const Services = lazy(() => import("./Pages/Services"));

// --- COMPOSANT INTERNE : SCROLL TO TOP ---
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// --- COMPOSANT INTERNE : SITE PROTECTION ---
// Bloque le clic droit, le drag d'image et certains raccourcis
const SiteProtection = () => {
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') e.preventDefault();
    };

    const handleKeyDown = (e) => {
      // Bloquer F12 et Ctrl+Shift+I
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return null;
};

function App() {
  // Un seul composant Header/Nav (mobile OU desktop) est monté à la fois :
  // évite de charger inutilement GlassSurface (SVG filters) et framer-motion
  // pour la version cachée par CSS (`hidden md:block` / `md:hidden`).
  const isMobile = useIsMobile();

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <SiteProtection />
        <DarkVeil hueShift={200} speed={1} noiseIntensity={0.01} />

      {/* FLOU TOP */}
      <GradualBlur
        position="top"
        target="page"
        height="3.5rem"
        strength={0.5}
        opacity={0.9}
        divCount={4}
        curve="smooth"
        animated={false}
        zIndex={30}
        style={{
          backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0) 100%)",
        }}
      />

      {/* FLOU BOTTOM */}
      {/* z-35 pour être au-dessus du carrousel (z-30) mais en dessous du footer (z-40) */}
      <GradualBlur
        position="bottom"
        target="page"
        height="3.5rem"
        strength={0.25}
        opacity={0.9}
        divCount={4}
        curve="smooth"
        animated={false}
        zIndex={35}
        style={{
          backgroundImage: "linear-gradient(to top, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0) 100%)",
        }}
      />

      {isMobile ? (
        <MobileHeader />
      ) : (
        <div className="relative z-50">
          <Header theme="light" />
        </div>
      )}

      {/* CONTENU PRINCIPAL */}
      <div className="relative z-10 pb-32 md:pb-0">
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Chargement...</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/work" element={<WorkPage />} />
            <Route path="/services" element={<Services />} />
          </Routes>
        </Suspense>
      </div>

      {/* CAROUSEL */}
      {/* z-30 pour être en dessous du blur bottom (z-35) */}
      <div className="relative z-30">
          <CarouselPartenaires />
      </div>

      {/* FOOTER */}
      {/* Mobile : z-20 (sous le flou z-35) + padding-bottom 24 (pour la navbar) */}
      {/* Desktop : z-40 (sur le flou z-35) + bg-white (cache le flou) + pas de padding */}
      <div className="relative z-20 md:z-40 md:bg-white pb-24 md:pb-0">
          <Footer />
      </div>

      {isMobile && <MobileNavBar />}

      </Router>
    </ErrorBoundary>
  );
}

export default App;
